import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AsyncPipe, DecimalPipe, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { BulkImportStatus, BulkImportType, ToggleButtonOption, UploadStatusOptions } from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import { MinuteFrequencyOption } from '@ngx-interval-data-grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { filter, take } from 'rxjs';
import { FileDragNDropDirective } from '../../../shared/directives/drop-zone.directive';
import { BulkUploadDialogInput } from '../../../shared/models/bulk-upload.model';
import { BulkUploadFileService } from '../../../shared/services/bulk-upload-file.service';
import { DataViewModelService } from '../../../shared/services/data-vm.service';
import { DateService } from '../../../shared/services/date.service';
import { GenerateCsvTemplateService } from '../../../shared/services/generate-csv-template.service';
import { InternalService } from '../../../shared/services/internal.service';
import { FileSizeUtil } from '../../../shared/utils/file-size.util';
import * as COMActions from '../../../store/actions/com.actions';
import * as COMSelectors from '../../../store/selectors/com.selectors';
import { BulkSelectionComponent } from '../bulk-selection/bulk-selection.component';
import { DateTimeRangePickerComponent } from '../date-time-range-picker/date-time-range-picker.component';

@Component({
  selector: 'dr-customer-offers-ui-bulk-upload',
  templateUrl: './bulk-upload.component.html',
  styleUrls: ['./bulk-upload.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, DecimalPipe, NgIf, NgClass, MatIconModule, MatFormFieldModule, TranslateModule, FileDragNDropDirective]
})
export class BulkUploadComponent implements OnInit {
  private readonly i18nStatusPrefix = 'COM.DIALOGS.BULK_UPLOAD.STATUS';

  readonly selectedToggleButtonSignal = signal<ToggleButtonOption>(ToggleButtonOption.TemplateDownload);
  @ViewChild('dateInputContainer') dateInputContainer!: ElementRef;

  private overlayRef!: OverlayRef;

  readonly toggleButtonOptions = ToggleButtonOption;

  readonly canDownloadTemplate = computed(() => {
    return !!this.selectedDateRangeSignal();
  });

  readonly selectedDateRangeSignal = computed<string>(() => {
    const startDate = this.selectedStartDate();
    const endDate = this.selectedEndDate();
    if (!startDate || !endDate) {
      return '';
    }
    const start = startDate.locale(this.userLocale()).format('L - hh:mm A');
    const end = endDate.locale(this.userLocale()).format('L - hh:mm A');
    return `${start} - ${end}`;
  });

  private selectedStartDate = signal<moment.Moment | undefined>(undefined);
  private selectedEndDate = signal<moment.Moment | undefined>(undefined);
  private readonly minuteFrequency: MinuteFrequencyOption = 15;
  private readonly defaultInitialDate: moment.Moment = moment().startOf('week').startOf('day');
  private readonly defaultEndDate: moment.Moment = moment().endOf('week').endOf('day');
  readonly uploadStatusOptions = UploadStatusOptions;
  private readonly storeUploadStatus = toSignal(this.store.select(COMSelectors.selectBulkImportUploadStatus), {
    initialValue: UploadStatusOptions.None
  });
  readonly uploadStatusSignal = computed<UploadStatusOptions>(() => {
    const storeStatus = this.storeUploadStatus();
    if (storeStatus !== UploadStatusOptions.None) {
      return storeStatus;
    }
    return this.fileSignal() ? UploadStatusOptions.Loaded : UploadStatusOptions.None;
  });
  readonly uploadProgressSignal = toSignal(this.store.select(COMSelectors.selectBulkImportUploadProgress), { initialValue: 0 });
  private readonly lastImportStatusSignal = toSignal(this.store.select(COMSelectors.selectBulkImportLastStatus), { initialValue: null });
  protected readonly bulkImportPercent$ = this.store.select(COMSelectors.selectBulkImportPercent);

  readonly uploadStatusLabelSignal = computed<string>(() => {
    const status = this.lastImportStatusSignal();
    switch (status) {
      case BulkImportStatus.Created:
        return this.translate.instant(`${this.i18nStatusPrefix}.QUEUED`);
      case BulkImportStatus.Processing:
        return this.translate.instant(`${this.i18nStatusPrefix}.PROCESSING`);
      default:
        return '';
    }
  });

  readonly dropZoneActiveSignal = signal<boolean>(false);
  readonly fileSignal = signal<File | null>(null);

  readonly fileSizeSignal = computed<string>(() => {
    const file = this.fileSignal();
    if (!file) {
      return '';
    }
    return FileSizeUtil.formatFileSize(file.size, 2);
  });

  protected userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');
  protected datePickerHint = computed(() => {
    const format = moment().locale(this.userLocale()).localeData().longDateFormat('L') ?? 'MM/DD/YYYY';
    return `${format} - hh:mm A - ${format} - hh:mm A`;
  });

  constructor(
    public dialogRef: MatDialogRef<BulkSelectionComponent>,
    private overlay: Overlay,
    private destroyRef: DestroyRef,
    private generateCsvTemplateService: GenerateCsvTemplateService,
    private translate: TranslateService,
    private store: Store,
    private bulkUploadFileService: BulkUploadFileService,
    private internalService: InternalService,
    private router: Router,
    private dataVMService: DataViewModelService,
    private dateService: DateService,
    @Inject(MAT_DIALOG_DATA)
    public data: BulkUploadDialogInput
  ) {
    this.destroyRef.onDestroy(() => {
      this.overlayRef?.dispose();
      this.bulkUploadFileService.clear();
    });
  }

  ngOnInit(): void {
    this.initializeData(this.data);
    this.closeOnImportSuccess();
  }

  private closeOnImportSuccess(): void {
    this.store
      .select(COMSelectors.selectBulkImportLastStatus)
      .pipe(
        filter(
          (status): status is BulkImportStatus =>
            status === BulkImportStatus.Succeeded || status === BulkImportStatus.Failed || status === BulkImportStatus.Partial
        ),
        take(1)
      )
      .subscribe(() => {
        this.store.dispatch(COMActions.bulkImportReset());

        // Adding this to refresh the cleared offers table once user has successfully uploaded data.
        const isCleared = this.router.url.includes('cleared/table');
        if (isCleared) {
          const selectedDates = this.dataVMService.getAppliedWeekRange();
          if (!selectedDates?.start || !selectedDates?.end) return;
          const selectedDatesISO = this.dateService.getISODate(selectedDates, this.data.timeZone);

          this.store.dispatch(
            COMActions.loadClearedOffers({
              registrationId: this.data.registrationId,
              startDate: selectedDatesISO.start,
              endDate: selectedDatesISO.end
            })
          );
        }
        this.dialogRef.close(true);
      });
  }

  setToggleOption(option: ToggleButtonOption) {
    this.selectedToggleButtonSignal.set(option);
  }

  onCloseClick() {
    this.bulkUploadFileService.clear();
    this.store.dispatch(COMActions.bulkImportReset());
    this.dialogRef.close();
  }

  onCalendarClick() {
    // Check if it's already open
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      return;
    }

    // Define how the overlay should behave and position itself
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.dateInputContainer)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8
        }
      ]);

    // Create the overlay
    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true // Allows clicking outside to close
    });

    // Close the overlay when the user clicks outside
    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.overlayRef.detach();
      });

    // Attach your custom calendar component to the overlay
    const portal = new ComponentPortal(DateTimeRangePickerComponent);
    const componentRef = this.overlayRef.attach(portal);

    componentRef.setInput('minuteFrequency', this.minuteFrequency);
    componentRef.setInput('minDate', this.selectedStartDate()?.toDate() ?? this.defaultInitialDate.toDate());
    componentRef.setInput('maxDate', this.selectedEndDate()?.toDate() ?? this.defaultEndDate.toDate());
    componentRef.setInput('initialStartDate', this.selectedStartDate());
    componentRef.setInput('initialEndDate', this.selectedEndDate());

    // Listen to the Outputs
    componentRef.instance.apply.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      this.selectedStartDate.set(result.startDate);
      this.selectedEndDate.set(result.endDate);
      this.overlayRef.detach(); // Close the overlay
    });

    componentRef.instance.cancel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.overlayRef.detach();
    });
  }

  private initializeData(data: { minDate: moment.Moment | undefined; maxDate: moment.Moment | undefined }): void {
    if (data.minDate) {
      this.selectedStartDate.set(data.minDate);
    }
    if (data.maxDate) {
      this.selectedEndDate.set(data.maxDate);
    }
  }

  onFilesDropped(fileList: File[]) {
    if (fileList.length === 0) return;
    const file = fileList[0];
    if (!this.validateFile(file)) return;
    this.fileSignal.set(file);
  }

  private validateFile(file: File): boolean {
    const isValidType = file.name.toLowerCase().endsWith('.csv');
    if (!isValidType) {
      return false;
    }
    return true;
  }

  toggleDropZoneActive(isActive: boolean) {
    this.dropZoneActiveSignal.set(isActive);
  }

  cleanSelectedFile() {
    this.fileSignal.set(null);
    this.bulkUploadFileService.clear();
    this.store.dispatch(COMActions.bulkImportReset());
  }

  onFileSelected(inputElement: HTMLInputElement) {
    if (inputElement.files) {
      // Array.from converts the FileList into an array of Files (File[])
      const filesArray = Array.from(inputElement.files);
      this.onFilesDropped(filesArray);
      // Reset the input value so the same file can be selected again if needed
      inputElement.value = '';
    }
  }

  onDownloadTemplate(): void {
    const startDate = this.selectedStartDate();
    const endDate = this.selectedEndDate();
    if (!startDate || !endDate) return;
    const base = {
      registrationId: this.data.registrationId,
      intervalFrequency: this.data.intervalFrequency,
      startDate,
      endDate,
      timeZone: this.data.timeZone,
      registrationName: this.data.registrationName
    };
    const config =
      this.data.importType === BulkImportType.Offers
        ? { ...base, importType: BulkImportType.Offers as const, offer_dimensions: this.data.offer_dimensions }
        : { ...base, importType: BulkImportType.Baselines as const };

    this.generateCsvTemplateService.generateAndDownload(config);
  }

  onConfirmUpload() {
    const file = this.fileSignal();
    if (!file) return;

    const importName = file.name.replace(/\.[ ^.]+$/, '');
    this.bulkUploadFileService.set(file);
    this.store.dispatch(
      COMActions.initiateBulkImport({
        registrationId: this.data.registrationId,
        importName,
        importType: this.data.importType
      })
    );
  }
}
